"use client";

import { ArrowUpRight } from "lucide-react";
import { ConversionCta } from "@/components/conversion/conversion-cta";
import { LandingAlertSubscribeForm } from "@/components/landing/landing-alert-subscribe-form";
import type { Locale } from "@/lib/i18n/config";

/**
 * The first-party unit a monetized slot falls back to when the session
 * frequency guardrail caps it.
 *
 * WHY THIS EXISTS. `NativeAd` used to render an empty reserved-height box for
 * every blocked slot, so a capped viewer got a ~152px band of dead whitespace
 * mid-feed. Measured over the 7 days to 2026-09-07, that was 91% of every
 * `feed_inline` slot and 97% of `category_footer` — the cap was doing far more
 * work than anyone intended, and its output was a hole. `AdSlot` had already
 * hit and solved exactly this (see the `houseCta` block in
 * `@/components/ads/ad-slot-client` and the 280px-hole note above it); this is
 * the same answer for the affiliate engine's slots.
 *
 * WHAT IT IS NOT. This is not the removed `LegacyAffiliateUnit` fallback. It
 * renders only for a FREQUENCY decision (`affiliate_cap` / `session_cap`) —
 * the viewer is fine, we simply decided not to sell them a third thing this
 * session. A monetization KILL (`engine.enabled: false`, no eligible arm, a Pro
 * member) still renders nothing at all, so the master switch keeps its meaning.
 *
 * TELEMETRY. This component emits NO impression event of its own, on purpose.
 * `NativeAd` already fires `monetized_suppressed` with the block reason on the
 * same mount, and this card renders for exactly `reason in (affiliate_cap,
 * session_cap)` — so that event IS the house denominator, and a second one
 * would be duplicate PostHog volume carrying no extra information (the
 * flag-quota blowout of 2026-08-28 is the standing reason not to add volume
 * casually). The numerator is each fill's own CTA event:
 * `house_slot_*_cta_clicked` via `ConversionCta`, or
 * `landing_role_alert_subscribed` with a `trigger_surface` of
 * `house_slot:<surface>`.
 *
 * Deliberately NOT `ad_impression`/`ad_click`: those D1 kinds are the affiliate
 * CTR numerator and denominator, and folding a house unit into them is the
 * exact shape of the #1649 CTR bug written up in `ad-slot-client.tsx`.
 */

/** Card shell — must track `OfferCard`'s silhouette (and the `NativeAd`
 * skeleton's) or the slot pops into a different shape when the guard resolves. */
const CARD_CLASS =
	"space-y-3 rounded-xl border border-border/60 bg-card p-5 text-left";

const LINK_CLASS =
	"group flex items-center justify-between gap-4 rounded-xl border border-border/60 bg-card px-5 py-4 transition-all hover:border-border/60 hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

type LinkCopy = { en: string; id: string; href: string; event: string };

/**
 * Per-surface house copy, keyed by MONETIZATION surface (not `AdSlot`'s slot
 * names). The strings are lifted from `ADVERTISE_COPY` in
 * `@/components/ads/ad-slot` so the two house rails say the same things in the
 * same voice; `href` is written `/en/…` there and localised at render.
 */
const LINK_COPY: Record<string, LinkCopy> = {
	job_sidebar: {
		en: "Stand out to employers — complete your profile",
		id: "Tampil lebih menonjol ke perusahaan — lengkapi profilmu",
		href: "/seeker",
		event: "house_slot_profile_cta_clicked",
	},
	category_sidebar: {
		en: "Stand out to employers — complete your profile",
		id: "Tampil lebih menonjol ke perusahaan — lengkapi profilmu",
		href: "/seeker",
		event: "house_slot_profile_cta_clicked",
	},
	category_footer: {
		en: "Get AI-matched to more roles like these",
		id: "Cocokkan ke lebih banyak lowongan seperti ini dengan AI",
		href: "/seeker",
		event: "house_slot_match_cta_clicked",
	},
};

const DEFAULT_LINK_COPY = LINK_COPY.job_sidebar as LinkCopy;

/** Surfaces whose house fill is the email job-alert form rather than a link. */
const ALERT_SURFACES = new Set(["feed_inline"]);

export function HouseSlotCard({
	surface,
	locale,
	topicTitle,
	topicSlug,
}: {
	surface: string;
	locale: Locale;
	/** Human label for the alert fill, e.g. the active category. */
	topicTitle?: string;
	/** Stable key for the alert fill, e.g. the active category id. */
	topicSlug?: string;
}) {
	const isId = locale !== "en";
	const l: "id" | "en" = isId ? "id" : "en";

	if (ALERT_SURFACES.has(surface)) {
		// The feed already carries `SearchCtaCard` ("Looking for something
		// specific?") at this same row, so a second "complete your profile" link
		// here would just be the same ask twice. An email alert is a different
		// ask, and it is the only one of the two that survives the visit.
		const roleTitle = topicTitle ?? (isId ? "kerja remote" : "remote");
		return (
			<div className={CARD_CLASS} data-testid="house-slot" data-house={surface}>
				<p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
					{isId ? "Alert lowongan" : "Job alerts"}
				</p>
				<p className="text-sm font-medium text-foreground">
					{isId
						? `Dapatkan email saat ada lowongan ${roleTitle} baru.`
						: `Get an email when new ${roleTitle} jobs go live.`}
				</p>
				<LandingAlertSubscribeForm
					roleTitle={roleTitle}
					roleSlug={topicSlug ?? "all-remote"}
					locale={l}
					isId={isId}
					triggerSurface={`house_slot:${surface}`}
				/>
			</div>
		);
	}

	const copy = LINK_COPY[surface] ?? DEFAULT_LINK_COPY;
	return (
		<div data-testid="house-slot" data-house={surface}>
			<ConversionCta
				href={`/${l}${copy.href}`}
				event={copy.event}
				surface={`house_slot:${surface}`}
				locale={l}
				className={LINK_CLASS}
			>
				<span className="min-w-0 space-y-0.5">
					<span className="block text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
						{isId ? "Dari Loker Dollar" : "From Loker Dollar"}
					</span>
					<span className="block text-sm font-medium text-foreground/70 transition-colors group-hover:text-foreground">
						{isId ? copy.id : copy.en}
					</span>
				</span>
				<ArrowUpRight
					className="h-4 w-4 shrink-0 text-muted-foreground/40 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-muted-foreground"
					aria-hidden="true"
				/>
			</ConversionCta>
		</div>
	);
}
